// libraries
import React, { useState, useEffect, useMemo } from "react";

//components
import Loader from "../../../../core/common/loader";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";

//helpers
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import api from "../../../../services/api";
import { toProperCase } from "../../../../helper/company_helper";
import { notify } from "../../../../services/toast.config";

interface HistoryEvent {
  id: string;
  type: string;
  title: string;
  description: string;
  fromValue?: string | null;
  toValue?: string | null;
  user: string;
  userAvatar?: string;
  notes?: string | null;
  actionType: string;
  timestamp: any;
}

interface AssetDetails {
  id: number;
  name: string;
  category: string;
  productId: number;
  brand: string;
  model: string;
  serialNumber: string;
  purchaseFrom: string;
  purchaseDate: string;
  warranty: string;
  imageUrl: any | null;
  assignedTo: string;
  assignedToId: number | null;
  assignedToType: string;
  assignedToDetails: any | null;
  assignedBy: string | null;
  assignedById: number | null;
  assignedDate: string | null;
  unassignedDate: string | null;
  status: string;
  location: string;
  companyId: number;
  companyType: string;
  isAssigned: boolean;
  canReassign: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
  avatar: string;
  designation: string;
  department_id: number;
}

interface Location {
  id: number;
  name: string;
  building: string;
  floor: string;
  room: string;
  company_id: number;
  status: number;
}

interface AssetHistoryModalProps {
  assetId: string;
  assetName: string;
  selectedHistoryAsset?: any;
}

const AssetHistoryModal: React.FC<AssetHistoryModalProps> = ({
  assetId,
  assetName,
  selectedHistoryAsset,
}) => {
  const signature = localStorage.getItem("signature");

  const [historyEvents, setHistoryEvents] = useState<HistoryEvent[]>([]);
  const [assetDetails, setAssetDetails] = useState<AssetDetails | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("history");
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);

  const fetchAssetData = async () => {
    try {
      setLoading(true);

      const dataToSend = {
        signature,
        id: selectedHistoryAsset?.id,
        company_type: selectedHistoryAsset?.company_type,
        company_id: selectedHistoryAsset?.company_id,
      };

      const response = await api.post(
        "assetService/getAssetDetailsWithHistory",
        dataToSend
      );
      const dataReceived = response?.data?.data?.data || [];

      setAssetDetails(dataReceived?.asset);
      setHistoryEvents(dataReceived?.history);
      setUsers(dataReceived?.users);
      setLocations(dataReceived?.locations);
    } catch (error) {
      notify.error("Error fetching Asset history");
    } finally {
      setLoading(false);
    }
  };

  // Fetch asset data from backend
  useEffect(() => {
    if (assetId) {
      fetchAssetData();
    }
  }, [assetId, selectedHistoryAsset]);

  const getEventIcon = (actionType: string) => {
    const iconConfig: Record<
      string,
      { icon: string; color: string; badge: string }
    > = {
      created: {
        icon: "ti-plus",
        color: "text-success",
        badge: "badge-success",
      },
      assigned: {
        icon: "ti-user-check",
        color: "text-primary",
        badge: "badge-primary",
      },
      unassigned: {
        icon: "ti-user-minus",
        color: "text-warning",
        badge: "badge-warning",
      },
      deleted: {
        icon: "ti-trash",
        color: "text-danger",
        badge: "badge-danger",
      },
      updated: { icon: "ti-edit", color: "text-info", badge: "badge-info" },
      maintenance: {
        icon: "ti-tool",
        color: "text-secondary",
        badge: "badge-secondaary",
      },
      ownership_changed: {
        icon: "ti-arrows-left-right",
        color: "text-dark",
        badge: "badge-dark",
      },
      lost: {
        icon: "ti-alert-triangle",
        color: "text-warning",
        badge: "badge-warning",
      },
      recovered: {
        icon: "ti-refresh-dot",
        color: "text-success",
        badge: "badge-success",
      },
    };
    return iconConfig[actionType] || { icon: "ti-clock", color: "text-muted" };
  };

  const toggleEventExpansion = (eventId: string) => {
    if (expandedEvent === eventId) {
      setExpandedEvent(null); // Close if already open
    } else {
      setExpandedEvent(eventId); // Open new one, closes previous
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-success-light text-success";
      case "inactive":
        return "bg-danger-light text-danger";
      case "maintenance":
      case "lost":
        return "bg-warning-light text-warning";
      default:
        return "bg-secondary-light text-secondary";
    }
  };

  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

  const filteredEvents = useMemo(() => {
    let filtered = historyEvents?.filter((event) => {
      return (
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.actionType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    // Then sort by timestamp
    return filtered?.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();

      if (sortOrder === "newest") {
        return dateB - dateA; // Newest first (descending)
      } else {
        return dateA - dateB; // Oldest first (ascending)
      }
    });
  }, [historyEvents, searchTerm, sortOrder]);

  return (
    <>
      {loading && <Loader />}

      <div className="modal fade" id="asset-history-modal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-header">
              <h4 className="modal-title">
                <i className="ti ti-history text-primary me-2"></i>
                Asset History -{" "}
                {selectedHistoryAsset &&
                  selectedHistoryAsset?.serial_no.toUpperCase()}{" "}
                {assetName}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close">
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="contact-grids-tab">
              <ul className="nav nav-underline" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${
                      activeTab == "history" ? "active" : ""
                    }`}
                    id="info-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#basic-info"
                    type="button"
                    role="tab"
                    aria-selected="false"
                    onClick={() => setActiveTab("history")}>
                    History
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${
                      activeTab == "details" ? "active" : ""
                    }`}
                    id="info-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#basic-info"
                    type="button"
                    role="tab"
                    aria-selected="false"
                    onClick={() => setActiveTab("details")}>
                    Details
                  </button>
                </li>
              </ul>
            </div>

            <div className="modal-body mt-0 py-0">
              {/* Asset Details Tab  */}
              {activeTab === "details" && (
                <div
                  className="asset-details-content py-2 px-3"
                  style={{ maxHeight: "420px", overflowY: "auto" }}>
                  {assetDetails ? (
                    <div className="row">
                      {/* Column 1: Static Details */}
                      <div className="col-lg-8">
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-header bg-transparent border-bottom">
                            <h6 className="mb-0 d-flex align-items-center">
                              <i className="ti ti-info-circle text-primary me-2"></i>
                              Basic Asset Information
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row g-1">
                              {/* Asset Category  */}
                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Category:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {assetDetails.category}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Asset Name:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {assetDetails.name}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Model:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {assetDetails.model}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Brand:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {assetDetails.brand}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Serial Number:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {assetDetails.serialNumber}
                                  </p>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Purchase From:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {assetDetails.purchaseFrom}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Purchase Date:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {humanReadableDate(
                                      assetDetails.purchaseDate
                                    )}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Warranty:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {humanReadableDate(assetDetails.warranty)}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Status:
                                  </label>
                                  <span
                                    className={`badge ${getStatusBadgeClass(
                                      assetDetails.status
                                    )} px-0`}>
                                    <i className="ti ti-point-filled me-1"></i>
                                    {assetDetails.status}
                                  </span>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Assignment Type:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {toProperCase(
                                      assetDetails.assignedToType?.replace(
                                        "_",
                                        " "
                                      )
                                    )}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    Assigned To:
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {assetDetails.assignedTo}
                                  </p>
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <label className="form-label text-muted small mb-0">
                                    {assetDetails.assignedToType !=
                                    "not_assigned"
                                      ? "Assigned Date:"
                                      : "Unassigned Date:"}
                                  </label>
                                  <p className="text-dark fw-medium mb-0 text-end">
                                    {`${
                                      assetDetails.assignedToType !=
                                      "not_assigned"
                                        ? assetDetails.assignedDate &&
                                          humanReadableDate(
                                            assetDetails?.assignedDate
                                          )
                                        : assetDetails.unassignedDate &&
                                          humanReadableDate(
                                            assetDetails?.unassignedDate
                                          )
                                    }`}
                                  </p>
                                </div>
                              </div>

                              {assetDetails.assignedToDetails && (
                                <div className="mb-1 p-3 bg-light-subtle rounded">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span className="badge bg-primary-light text-primary px-2 py-1 small">
                                      <i className="ti ti-crown me-1"></i>
                                      Assigned By
                                    </span>
                                    <div className="d-flex align-items-center">
                                      <div>
                                        <label className="form-label text-muted small mb-0">
                                          {assetDetails.assignedBy || "System"}
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Column 2: Image, View Option, and Company Details */}
                      <div className="col-lg-4 d-flex flex-column">
                        {/* Product Image and View Option */}

                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-header bg-transparent border-bottom">
                            <h6 className="mb-0 d-flex align-items-center">
                              <i className="ti ti-photo text-info me-2"></i>
                              Asset Image
                            </h6>
                          </div>
                          <div className="card-body text-center">
                            <div className="image-wrapper mb-1">
                              <ImageWithBasePath
                                className="rounded-2"
                                src={
                                  !assetDetails?.imageUrl?.url ||
                                  assetDetails?.imageUrl?.url === "null"
                                    ? "assets/img/products/product-icon.png"
                                    : assetDetails?.imageUrl?.url
                                }
                                alt="product-img"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ti ti-package-off display-4 text-muted mb-3"></i>
                      <h6 className="text-muted">Asset details not found</h6>
                    </div>
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === "history" && (
                <div
                  className="history-timeline px-3"
                  style={{ maxHeight: "420px", overflowY: "auto" }}>
                  {/* Search Bar */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <div className="input-group input-group-flat d-inline-flex me-1">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="ti ti-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search history events..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Sort Options */}
                    <div className="col-6">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="ti ti-sort-descending text-muted"></i>
                        </span>
                        <select
                          className="form-select fs-12 border border-1"
                          value={sortOrder}
                          onChange={(e) =>
                            setSortOrder(e.target.value as "newest" | "oldest")
                          }>
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {filteredEvents?.map((event) => {
                    const eventConfig = getEventIcon(event.actionType);
                    const isExpanded = expandedEvent === event.id;

                    return (
                      <div key={event.id} className="mb-3">
                        {/* Single Card Container */}
                        <div className="card border-0 shadow-sm">
                          {/* Main Event Row - Always Visible */}
                          <div
                            className="card-body p-3 cursor-pointer"
                            onClick={() => toggleEventExpansion(event.id)}>
                            <div className="d-flex align-items-start">
                              {/* Event Icon */}
                              <div className="me-3 mt-1">
                                <span
                                  className={`d-flex align-items-center badge badge-sm ${eventConfig.badge}`}>
                                  <i
                                    className={`fs-16 ti ${eventConfig.icon}`}></i>
                                </span>
                              </div>

                              {/* Event Content */}
                              <div className="flex-grow-1 min-width-0">
                                {/* Event Title & Time */}
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <h6 className="mb-0 text-dark fw-semibold">
                                    {event.title}
                                  </h6>
                                  <div className="text-end ms-3 flex-shrink-0">
                                    <small className="fs-11 fw-bold text-info">
                                      {humanReadableDate(event.timestamp)}
                                    </small>
                                    <i
                                      className={`ti ${
                                        isExpanded
                                          ? "ti-chevron-up"
                                          : "ti-chevron-down"
                                      } text-muted ms-2`}></i>
                                  </div>
                                </div>

                                {/* Event Description */}
                                <p className="text-muted mb-2 small">
                                  {event.description}
                                </p>

                                {/* User Info & Type */}
                                <div className="d-flex align-items-center">
                                  <div className="avatar avatar-xs me-2">
                                    <img
                                      src={
                                        event.userAvatar ||
                                        "assets/img/profiles/default-avatar.jpg"
                                      }
                                      alt={event.user}
                                      className="rounded-circle"
                                    />
                                  </div>
                                  <span className="small text-muted me-2">
                                    {event.user}
                                  </span>
                                  <span className="small text-muted me-2">
                                    •
                                  </span>
                                  <span className="badge badge-soft-info text-capitalize small">
                                    {event.actionType.replace("_", " ")}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Expandable Details - Toggle Visibility */}
                          {isExpanded && (
                            <div className="border-top">
                              <div className="card-body bg-light-subtle pt-3">
                                <div className="row">
                                  {/* Details and Notes */}
                                  <div className="col-md-12">
                                    <h6 className="fw-semibold mb-2 text-secondary">
                                      <i className="ti ti-info-circle me-1"></i>
                                      Event Details
                                    </h6>
                                    <p className="text-muted mb-3">
                                      {event.description}
                                    </p>

                                    {event.notes && (
                                      <div className="mb-3">
                                        <h6 className="fw-semibold mb-2 text-secondary">
                                          <i className="ti ti-note me-1"></i>
                                          Notes
                                        </h6>
                                        <div className="bg-white p-2 rounded border">
                                          <p className="text-muted mb-0 small">
                                            {event.notes}
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Changes Made */}
                                    {event.fromValue && event.toValue && (
                                      <div className="mb-3">
                                        <h6 className="fw-semibold mb-2 text-secondary">
                                          <i className="ti ti-arrows-exchange me-1"></i>
                                          Changes Made
                                        </h6>
                                        <div className="d-flex align-items-center justify-content-center gap-3 bg-white p-2 rounded">
                                          <div className="text-center">
                                            <small className="d-block fw-semibold text-muted mb-1">
                                              FROM
                                            </small>
                                            <span className="badge bg-danger-light text-danger px-2 py-1">
                                              {event.fromValue}
                                            </span>
                                          </div>
                                          <div className="badge badge-primary">
                                            <i className="ti ti-arrow-right"></i>
                                          </div>
                                          <div className="text-center">
                                            <small className="d-block fw-semibold text-muted mb-1">
                                              TO
                                            </small>
                                            <span className="badge bg-success-light text-success px-2 py-1">
                                              {event.toValue}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Meta Data */}
                                  <div className="col-md-12">
                                    <div className="bg-white border rounded p-3">
                                      <h6 className="fw-semibold mb-2 text-secondary">
                                        <i className="ti ti-info-square me-1"></i>
                                        Event Metadata
                                      </h6>
                                      <div className="row g-2">
                                        <div className="col-6">
                                          <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                            <span className="text-muted small">
                                              Event ID:
                                            </span>
                                            <span className="fw-medium small">
                                              #{event.id}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="col-6">
                                          <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                            <span className="text-muted small">
                                              Type:
                                            </span>
                                            <span className="fw-medium small text-capitalize">
                                              {event.type}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="col-6">
                                          <div className="d-flex justify-content-between align-items-center py-1">
                                            <span className="text-muted small">
                                              User:
                                            </span>
                                            <span className="fw-medium small">
                                              {event.user}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="col-6">
                                          <div className="d-flex justify-content-between align-items-center py-1">
                                            <span className="text-muted small">
                                              Action:
                                            </span>
                                            <span className="fw-medium small text-capitalize">
                                              {event.actionType}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="modal-footer border-top">
              <button
                type="button"
                className="btn btn-primary btn-sm me-2"
                data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssetHistoryModal;
